import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from "recharts";
import { useWell } from "../WellContext";           
import { useAIAdvice } from "../useAIAdvice";

import { API } from "../config/api";
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};

const CHART_MARGIN_DEFAULT = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_HISTO   = { top:8, right:14, left:-10, bottom:36 };

function ChartCard({ title, subtitle, children, height=260, delay=0, accentColor }) {
  const ac=accentColor||C.cyan;
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"20px 16px 20px", animation:`fade-up 0.5s ease ${delay}s both`,
      position:"relative", overflow:"visible"
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function PulseDot({ color=C.green, size=10 }) {
  return (
    <span style={{ position:"relative", display:"inline-block", width:size, height:size, flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, animation:"pulse-ring 1.8s ease-out infinite", opacity:0 }}/>
      <span style={{ position:"absolute", inset:2, borderRadius:"50%", background:color, boxShadow:`0 0 6px ${color}` }}/>
    </span>
  );
}

export default function TensorBoardPage({ TTip }) {
  const { selectedWell, apiUrl } = useWell();

  const [tbInfo,   setTbInfo]   = useState(null);
  const [scalars,  setScalars]  = useState(null);
  const [hists,    setHists]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selTag,   setSelTag]   = useState(null);
  const [selHist,  setSelHist]  = useState(null);
  const [wellTimeseries, setWellTimeseries] = useState([]);
  const [wellSummary,    setWellSummary]    = useState({});
  const [wellArrStats,   setWellArrStats]   = useState(null);

  const wellShort = selectedWell ? selectedWell.split("/").pop() : null;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [info,sc,hi,ts,sum,arr] = await Promise.all([
        axios.get(`${API}/tensorboard/info`),
        axios.get(`${API}/tensorboard/scalars`),
        axios.get(`${API}/tensorboard/histograms`),
        apiUrl ? axios.get(apiUrl("/cycles/timeseries?limit=200")) : Promise.resolve({ data:{ data:[] } }),
        apiUrl ? axios.get(apiUrl("/cycles/summary"))              : Promise.resolve({ data:{} }),
        apiUrl ? axios.get(apiUrl("/cycles/arrival-stats"))        : Promise.resolve({ data:null }),
      ]);
      setTbInfo(info.data); setScalars(sc.data); setHists(hi.data);
      setWellTimeseries(ts.data?.data||[]);
      setWellSummary(sum.data||{});
      setWellArrStats(arr.data||null);
      setSelTag(null); setSelHist(null);
    } catch(e) { setError(e.message); }
    setLoading(false);
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if(scalars?.tags?.length)             setSelTag(scalars.tags[0]);
    if(hists?.histograms) { const keys=Object.keys(hists.histograms); if(keys.length) setSelHist(keys[0]); }
  }, [scalars, hists, selectedWell]);

  const tagGroups = {};
  if(scalars?.tags) scalars.tags.forEach(tag=>{
    const prefix=tag.split("/")[0];
    if(!tagGroups[prefix]) tagGroups[prefix]=[];
    tagGroups[prefix].push(tag);
  });

  const scalarChartData = useMemo(() => {
    if(!selTag) return [];
    const isWellTag=selTag.startsWith("Well/");
    if(isWellTag&&wellTimeseries?.length) {
      const colName=selTag.replace("Well/","");
      return wellTimeseries.filter(row=>row[colName]!=null).map((row,i)=>({ step:i, value:row[colName], _actualFieldKey:"value", time:row.time||null }));
    }
    if(scalars?.scalars?.[selTag]) return scalars.scalars[selTag].map(e=>({ step:e.step, value:e.value, _actualFieldKey:"value" }));
    return [];
  }, [selTag, wellTimeseries, scalars]);

  const histChartData = useMemo(() => {
    if(!selHist) return [];
    if(selHist==="Histograms/ArrivalVelocity"&&wellArrStats?.bins?.length) {
      return wellArrStats.bins.map((b,i)=>({ bin:Number(b).toFixed(1), count:wellArrStats.counts[i], _actualFieldKey:"count" }));
    }
    const colMap = { "Histograms/CsgAtOpen":"CsgAtOpen", "Histograms/TbgAtOpen":"TbgAtOpen", "Histograms/LineAtOpen":"LineAtOpen" };
    const col=colMap[selHist];
    if(col&&wellTimeseries?.length) {
      const vals=wellTimeseries.map(r=>r[col]).filter(v=>v!=null&&isFinite(v));
      if(vals.length) {
        const min=Math.min(...vals), max=Math.max(...vals), nBins=20, step=(max-min)/nBins||1;
        const counts=Array(nBins).fill(0);
        vals.forEach(v=>{ const idx=Math.min(Math.floor((v-min)/step),nBins-1); counts[idx]++; });
        return counts.map((count,i)=>({ bin:(min+i*step).toFixed(1), count, _actualFieldKey:"count" }));
      }
    }
    if(hists?.histograms?.[selHist]) {
      return hists.histograms[selHist].left_edges.map((edge,i)=>({ bin:edge.toFixed(1), count:hists.histograms[selHist].counts[i], _actualFieldKey:"count" }));
    }
    return [];
  }, [selHist, wellArrStats, wellTimeseries, hists]);

  const wellMeanRows = useMemo(() => {
    if(!wellSummary) return [];
    return Object.entries(wellSummary)
      .filter(([,v])=>v&&typeof v.mean==="number")
      .map(([col,v])=>({ name:col, val:v.mean }))
      .sort((a,b)=>a.name.localeCompare(b.name));
  }, [wellSummary]);

  if(loading) return <div style={{ textAlign:"center", padding:60, fontFamily:MONO, fontSize:13, color:C.muted }}>Loading TensorBoard data...</div>;
  if(error)   return <div style={{ background:C.card, border:`1px solid ${C.red}44`, borderRadius:14, padding:28, fontFamily:MONO, fontSize:12, color:C.red }}>{error}</div>;

  const GROUP_COLORS=[C.cyan,C.teal,C.amber,C.pink,C.purple,C.blue,C.green,C.red];
  const wellLabel=wellShort||"All Wells";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, animation:"fade-up 0.4s ease both" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <PulseDot color={tbInfo?.status==="ready"?C.green:C.amber} size={10}/>
          <span style={{ fontFamily:MONO, fontSize:12, color:C.text }}>TensorBoard Logs</span>
          <span style={{ background:tbInfo?.status==="ready"?`${C.green}22`:`${C.amber}22`, color:tbInfo?.status==="ready"?C.green:C.amber, fontFamily:MONO, fontSize:10, padding:"2px 10px", borderRadius:20 }}>{tbInfo?.status?.toUpperCase()??"—"}</span>
          <span style={{ background:`${C.cyan}18`, color:C.cyan, fontFamily:MONO, fontSize:10, padding:"2px 12px", borderRadius:20, border:`1px solid ${C.cyan}44` }}>{wellLabel}</span>
        </div>
        <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
          {[
            ["Log Files",      tbInfo?.log_files??"—",                         C.cyan],
            ["Scalar Tags",    scalars?.tags?.length??"—",                     C.teal],
            ["Histogram Tags", Object.keys(hists?.histograms??{}).length,      C.amber],
            ["Well Cycles",    wellTimeseries?.length??"—",                    C.green],
          ].map(([label,val,color])=>(
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:MONO, fontSize:18, fontWeight:700, color }}>{val}</div>
              <div style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>{label}</div>
            </div>
          ))}
        </div>
        <button onClick={load} style={{ background:`${C.purple}22`, color:C.purple, border:`1px solid ${C.purple}44`, borderRadius:8, padding:"6px 16px", fontFamily:MONO, fontSize:11, cursor:"pointer", fontWeight:700 }}>↻ Refresh</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"240px 1fr", gap:20 }}>
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"18px 14px", maxHeight:520, overflowY:"auto" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, marginBottom:14 }}>Scalar Tags</div>
          {Object.entries(tagGroups).map(([group,tags],gi)=>(
            <div key={group} style={{ marginBottom:12 }}>
              <div style={{ fontFamily:MONO, fontSize:9, color:GROUP_COLORS[gi%GROUP_COLORS.length], letterSpacing:1.5, textTransform:"uppercase", marginBottom:6, paddingBottom:4, borderBottom:`1px solid ${C.border}` }}>{group}</div>
              {tags.map(tag=>{
                const short=tag.split("/").slice(1).join("/")||tag;
                const sel=tag===selTag;
                return (
                  <div key={tag} onClick={()=>setSelTag(tag)} style={{
                    padding:"5px 8px", borderRadius:6, cursor:"pointer", marginBottom:2,
                    background:sel?`${C.cyan}18`:"transparent",
                    border:`1px solid ${sel?C.cyan+"44":"transparent"}`,
                    fontFamily:MONO, fontSize:10, color:sel?C.cyan:C.muted,
                    transition:"all 0.15s ease",
                    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                  }}>{short}</div>
                );
              })}
            </div>
          ))}
        </div>

        <ChartCard title={selTag?`Scalar: ${selTag}`:"Select a tag on the left"} subtitle={`${scalarChartData.length} data points — ${wellLabel}`} accentColor={C.cyan} height={460} delay={0.1}>
          {scalarChartData.length>0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={scalarChartData} margin={CHART_MARGIN_DEFAULT}>
                <defs>
                  <linearGradient id="gtbscalar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.cyan} stopOpacity={0.35}/><stop offset="95%" stopColor={C.cyan} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="step" tick={{ fill:C.muted, fontSize:10, fontFamily:MONO }} tickLine={false} label={{ value:"Step / Cycle", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
                <YAxis tick={{ fill:C.muted, fontSize:10, fontFamily:MONO }} tickLine={false} axisLine={false}/>
                <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
                <Area type="monotone" dataKey="value" name={selTag?.split("/").pop()} stroke={C.cyan} fill="url(#gtbscalar)" strokeWidth={2} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontFamily:MONO, fontSize:12, color:C.muted }}>
              {wellTimeseries?"No data for selected tag / well":"Select a scalar tag"}
            </div>
          )}
        </ChartCard>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <ChartCard title="Histograms" subtitle={`Distribution of logged variables — ${wellLabel}`} accentColor={C.amber} height={300} delay={0.15}>
          <div style={{ marginBottom:8, display:"flex", gap:6, flexWrap:"wrap" }}>
            {Object.keys(hists?.histograms??{}).map(tag=>(
              <button key={tag} onClick={()=>setSelHist(tag)} style={{
                background:selHist===tag?`${C.amber}22`:"transparent",
                color:selHist===tag?C.amber:C.muted,
                border:`1px solid ${selHist===tag?C.amber+"55":C.border}`,
                borderRadius:6, padding:"3px 10px", fontFamily:MONO, fontSize:10, cursor:"pointer",
              }}>{tag.split("/").pop()}</button>
            ))}
          </div>
          {histChartData.length>0 ? (
            <ResponsiveContainer width="100%" height="78%">
              <BarChart data={histChartData} margin={CHART_MARGIN_HISTO}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="bin" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} label={{ value:"Bin", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
                <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
                <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
                <Bar dataKey="count" name="Count" fill={C.amber} fillOpacity={0.8} radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"78%", fontFamily:MONO, fontSize:12, color:C.muted }}>No histogram data for {wellLabel}</div>
          )}
        </ChartCard>

        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", animation:"fade-up 0.5s ease 0.2s both", maxHeight:360, overflowY:"auto" }}>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, marginBottom:4 }}>Stats / Mean — {wellLabel}</div>
          <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:12 }}>Computed from {wellTimeseries?.length??0} cycles for the selected well</div>
          {wellMeanRows.length>0 ? wellMeanRows.map(({ name, val })=>(
            <div key={name} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>{name}</span>
              <span style={{ fontFamily:MONO, fontSize:10, color:C.teal }}>{val!=null?val.toFixed(3):"—"}</span>
            </div>
          )) : <div style={{ color:C.muted, fontFamily:MONO, fontSize:12 }}>No data</div>}
        </div>
      </div>
    </div>
  );
}